import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { EditorHeader } from "./EditorHeader";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { EditorLoading } from "./shared/EditorLoading";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

type BusinessSummary = {
  id: string;
  title: string;
  welcome_message: string;
  story: string;
  mission: string;
  vision: string;
  created_at: string;
  updated_at: string;
} | null;

export const BusinessSummaryEditor = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coreValues, setCoreValues] = useState<any[]>([]);

  const { data: businessSummary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['business-summary'],
    queryFn: async () => {
      console.log('Fetching business summary data...');
      const { data, error } = await supabase
        .from('business_summary')
        .select('*')
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
      
      console.log('Fetched business summary:', data);
      return data as BusinessSummary;
    },
  });

  const { data: coreValuesData, isLoading: isCoreValuesLoading } = useQuery({
    queryKey: ['core-values'],
    queryFn: async () => {
      console.log('Fetching core values...');
      const { data, error } = await supabase
        .from('core_values')
        .select('*')
        .order('order_index');
      
      if (error) throw error;
      console.log('Fetched core values:', data);
      setCoreValues(data || []);
      return data;
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    console.log('Starting save operation...');

    const formData = new FormData(e.currentTarget);
    
    try {
      // Save business summary
      const summaryUpdates = {
        title: String(formData.get('title') || ""),
        welcome_message: String(formData.get('welcome_message') || ""),
        story: String(formData.get('story') || ""),
        mission: String(formData.get('mission') || ""),
        vision: String(formData.get('vision') || ""),
        updated_at: new Date().toISOString(),
      };

      console.log('Saving business summary:', summaryUpdates);

      if (businessSummary?.id) {
        const { error: summaryError } = await supabase
          .from('business_summary')
          .update(summaryUpdates)
          .eq('id', businessSummary.id);

        if (summaryError) throw summaryError;
      } else {
        const { error: summaryError } = await supabase
          .from('business_summary')
          .insert([summaryUpdates]);

        if (summaryError) throw summaryError;
      }

      // Save core values
      const coreValuePromises = coreValues.map(async (value, index) => {
        const coreValueData = {
          title: String(formData.get(`core-value-title-${index}`) || ""),
          description: String(formData.get(`core-value-description-${index}`) || ""),
          emoji: String(formData.get(`core-value-emoji-${index}`) || ""),
          order_index: index,
        };

        console.log(`Saving core value ${index}:`, coreValueData);

        if (value.id) {
          return supabase
            .from('core_values')
            .update(coreValueData)
            .eq('id', value.id);
        } else {
          return supabase
            .from('core_values')
            .insert([coreValueData]);
        }
      });

      await Promise.all(coreValuePromises);

      toast({
        title: "Success",
        description: "Content has been updated",
      });
    } catch (error) {
      console.error('Error saving data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update content",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addCoreValue = () => {
    setCoreValues([
      ...coreValues,
      { title: "", description: "", emoji: "", order_index: coreValues.length }
    ]);
  };

  const removeCoreValue = (index: number) => {
    const newValues = [...coreValues];
    newValues.splice(index, 1);
    newValues.forEach((value, idx) => {
      value.order_index = idx;
    });
    setCoreValues(newValues);
  };

  if (isSummaryLoading || isCoreValuesLoading) {
    return <EditorLoading />;
  }

  const defaultValues = {
    title: "",
    welcome_message: "",
    story: "",
    mission: "",
    vision: "",
  };

  return (
    <div className="space-y-6">
      <EditorHeader title="Business Summary Editor" />
      <EditorForm onSubmit={handleSave} isSubmitting={isSubmitting}>
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="welcome">Welcome Message</TabsTrigger>
            <TabsTrigger value="story">Story</TabsTrigger>
            <TabsTrigger value="mission-vision">Mission & Vision</TabsTrigger>
            <TabsTrigger value="core-values">Core Values</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <EditorField
              label="Title"
              name="title"
              defaultValue={businessSummary?.title || defaultValues.title}
              required
            />
          </TabsContent>

          <TabsContent value="welcome" className="space-y-4">
            <EditorField
              label="Welcome Message"
              name="welcome_message"
              defaultValue={businessSummary?.welcome_message || defaultValues.welcome_message}
              type="textarea"
              rows={8}
              required
            />
          </TabsContent>

          <TabsContent value="story" className="space-y-4">
            <EditorField
              label="Story"
              name="story"
              defaultValue={businessSummary?.story || defaultValues.story}
              type="textarea"
              rows={5}
              required
            />
          </TabsContent>

          <TabsContent value="mission-vision" className="space-y-4">
            <EditorField
              label="Mission"
              name="mission"
              defaultValue={businessSummary?.mission || defaultValues.mission}
              type="textarea"
              rows={3}
              required
            />
            <EditorField
              label="Vision"
              name="vision"
              defaultValue={businessSummary?.vision || defaultValues.vision}
              type="textarea"
              rows={3}
              required
            />
          </TabsContent>

          <TabsContent value="core-values" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Core Values</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCoreValue}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Value
              </Button>
            </div>
            <div className="space-y-6">
              {coreValues.map((value, index) => (
                <div key={index} className="relative p-4 border rounded-lg space-y-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => removeCoreValue(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <EditorField
                    label="Emoji"
                    name={`core-value-emoji-${index}`}
                    defaultValue={value.emoji}
                    required
                  />
                  <EditorField
                    label="Title"
                    name={`core-value-title-${index}`}
                    defaultValue={value.title}
                    required
                  />
                  <EditorField
                    label="Description"
                    name={`core-value-description-${index}`}
                    defaultValue={value.description}
                    type="textarea"
                    rows={3}
                    required
                  />
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </EditorForm>
    </div>
  );
};